// Call Transfer Statistics Component
// Displays analytics for voice assistant call transfers
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Phone, PhoneForwarded, PhoneMissed, Clock, TrendingUp, 
  BarChart3, PieChart, Calendar, RefreshCw, Users, Loader2,
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallTransferStats = ({ token, professionalId = null }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [selectedProfessional, setSelectedProfessional] = useState(professionalId || 'all');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStats();
  }, [dateRange, selectedProfessional]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      if (dateRange === '7d') startDate.setDate(endDate.getDate() - 7);
      else if (dateRange === '30d') startDate.setDate(endDate.getDate() - 30);
      else if (dateRange === '90d') startDate.setDate(endDate.getDate() - 90);
      
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', endDate.toISOString().split('T')[0]);
      
      if (selectedProfessional && selectedProfessional !== 'all') {
        params.append('professional_id', selectedProfessional);
      }

      const res = await axios.get(`${API_URL}/api/analytics/call-transfers?${params}`, { headers });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching call transfer stats:', error);
      // Set default stats if endpoint not available
      setStats({
        total_calls: 0,
        transferred: 0,
        voicemail: 0,
        abandoned: 0,
        success_rate: 0,
        avg_duration_seconds: 0,
        peak_hours: [],
        categories: [],
        daily_trend: [],
        by_professional: []
      });
    }
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getSuccessColor = (rate) => {
    if (rate >= 80) return 'text-green-400';
    if (rate >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="call-transfer-stats">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <PhoneForwarded className="w-6 h-6 text-blue-400" />
            Statistiques Transferts d'Appels
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Analytics de l'assistant vocal Vapi
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="7d" className="text-white">7 jours</SelectItem>
              <SelectItem value="30d" className="text-white">30 jours</SelectItem>
              <SelectItem value="90d" className="text-white">90 jours</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchStats}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats?.total_calls || 0}</p>
                <p className="text-xs text-blue-300">Total Appels</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <PhoneForwarded className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats?.transferred || 0}</p>
                <p className="text-xs text-green-300">Transférés</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{formatDuration(stats?.avg_duration_seconds)}</p>
                <p className="text-xs text-yellow-300">Durée Moyenne</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className={`text-3xl font-bold ${getSuccessColor(stats?.success_rate || 0)}`}>
                  {(stats?.success_rate || 0).toFixed(1)}%
                </p>
                <p className="text-xs text-purple-300">Taux de Succès</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Categories Breakdown */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-400" />
              Par Catégorie de Demande
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.categories && stats.categories.length > 0 ? (
              <div className="space-y-3">
                {stats.categories.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{cat.name || cat.category}</span>
                      <Badge className="bg-slate-600 text-slate-200">{cat.count} appels</Badge>
                    </div>
                    <div className="w-32 bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${(cat.count / (stats.total_calls || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Phone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucune donnée de catégorie disponible</p>
                <p className="text-xs mt-1">Les stats apparaîtront après les premiers appels</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Peak Hours */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Heures de Pointe
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.peak_hours && stats.peak_hours.length > 0 ? (
              <div className="space-y-2">
                {stats.peak_hours.slice(0, 6).map((hour, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-white font-medium">
                      {String(hour.hour).padStart(2, '0')}:00 - {String(hour.hour + 1).padStart(2, '0')}:00
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full transition-all"
                          style={{ width: `${(hour.count / (stats.peak_hours[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-slate-400 w-12 text-right">{hour.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucune donnée d'heures disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outcome Breakdown */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-400" />
              Résultats des Appels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-white">Transférés avec succès</span>
                </div>
                <Badge className="bg-green-500/20 text-green-400">
                  {stats?.transferred || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <span className="text-white">Messagerie vocale</span>
                </div>
                <Badge className="bg-yellow-500/20 text-yellow-400">
                  {stats?.voicemail || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-white">Abandonnés</span>
                </div>
                <Badge className="bg-red-500/20 text-red-400">
                  {stats?.abandoned || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PhoneMissed className="w-5 h-5 text-slate-400" />
                  <span className="text-white">Rappel demandé</span>
                </div>
                <Badge className="bg-slate-500/20 text-slate-400">
                  {stats?.callback_requested || 0}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* By Professional (Admin view) */}
        {stats?.by_professional && stats.by_professional.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Par Professionnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {stats.by_professional.map((pro, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-700/30 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{pro.name || `Pro ${idx + 1}`}</p>
                      <p className="text-xs text-slate-400">{pro.specialty || 'Généraliste'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">{pro.total_calls}</p>
                      <p className={`text-xs ${getSuccessColor(pro.success_rate)}`}>
                        {pro.success_rate?.toFixed(0)}% succès
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Daily Trend Chart */}
      {stats?.daily_trend && stats.daily_trend.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              Tendance Quotidienne
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {stats.daily_trend.map((day, idx) => {
                const maxCalls = Math.max(...stats.daily_trend.map(d => d.calls || 0));
                const height = maxCalls > 0 ? ((day.calls || 0) / maxCalls) * 100 : 0;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-blue-500/60 rounded-t transition-all hover:bg-blue-500"
                      style={{ height: `${Math.max(height, 4)}%` }}
                      title={`${day.date}: ${day.calls} appels`}
                    />
                    <span className="text-xs text-slate-500 truncate w-full text-center">
                      {new Date(day.date).toLocaleDateString('fr', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!stats || stats.total_calls === 0) && (
        <Card className="bg-slate-800/30 border-slate-700 border-dashed">
          <CardContent className="p-12 text-center">
            <PhoneForwarded className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-white text-lg font-medium mb-2">Aucune donnée de transfert</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Les statistiques apparaîtront ici une fois que l'assistant vocal Vapi 
              commencera à traiter des appels avec transfert.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CallTransferStats;
